import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Heading,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Select,
  IconButton,
  FormControl,
  FormLabel,
  Divider,
  Image,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { DragHandleIcon, DeleteIcon } from '@chakra-ui/icons';
import { FiExternalLink } from 'react-icons/fi';
import { Icon } from '@chakra-ui/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import QRCode from 'qrcode';
import { fightService } from '../../services/fightService';
import { useDataStore } from '../../contexts/DataStoreContext';
import { AddFightForm } from './AddFightForm';
import { FighterEditForm } from './FighterEditForm';
import { ImageUpload } from '../Common/ImageUpload';
import { eventService } from '../../services/eventService';

const SortableFightItem = ({ fight, index, onWinnerSelect, onEditFighter, onEditFight, onDeleteFight, loading, eventId }) => {
  const dataStore = useDataStore();
  const [voteStats, setVoteStats] = useState([]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: fight.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Subscribe to real-time vote statistics
  useEffect(() => {
    if (!eventId || !fight?.id) return;

    // Get vote stats from DataStore
    const stats = dataStore.getVotesForFight(eventId, fight.id);
    setVoteStats(stats);
  }, [eventId, fight?.id, dataStore.votes, dataStore]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'blue';
      case 'active': return 'green';
      case 'completed': return 'gray';
      default: return 'gray';
    }
  };

  // Helper to get vote count for a specific fighter
  const getVoteCount = (fighterId) => {
    const stat = voteStats.find(s => s.fighterId === fighterId);
    return stat ? stat.votes : 0;
  };

  const getTotalVotes = () => {
    return voteStats.reduce((sum, stat) => sum + stat.votes, 0);
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      p={4}
      borderWidth={1}
      borderRadius="md"
      bg="dark.700"
      _hover={{ bg: 'dark.600' }}
    >
      <HStack justify="space-between" align="start" spacing={4}>
        <HStack spacing={3} flex={1}>
          <IconButton
            {...attributes}
            {...listeners}
            icon={<DragHandleIcon />}
            size="sm"
            variant="ghost"
            cursor="grab"
            _active={{ cursor: 'grabbing' }}
            aria-label="Drag to reorder"
          />

          <VStack align="start" spacing={1} flex={1}>
            <HStack spacing={2}>
              <Text fontWeight="bold">Fight {index + 1}:</Text>
              <Button
                variant="link"
                size="sm"
                color="blue.400"
                fontWeight="bold"
                onClick={() => onEditFighter(fight.fighter1)}
                _hover={{ color: 'blue.300' }}
              >
                {fight.fighter1?.name}
              </Button>
              <Text fontWeight="bold">vs</Text>
              <Button
                variant="link"
                size="sm"
                color="blue.400"
                fontWeight="bold"
                onClick={() => onEditFighter(fight.fighter2)}
                _hover={{ color: 'blue.300' }}
              >
                {fight.fighter2?.name}
              </Button>
            </HStack>
            <HStack spacing={4} fontSize="sm" color="gray.400">
              <Button
                variant="link"
                size="xs"
                color="gray.400"
                onClick={() => onEditFight(fight)}
                _hover={{ color: 'gray.300' }}
              >
                {fight.weightClass} • {fight.rounds} rounds
              </Button>
              <Badge colorScheme={getStatusColor(fight.status)}>
                {fight.winnerId ?
                  (fight.winnerId === 'draw' ? 'Draw' :
                    fight.winnerId === 'no-contest' ? 'No Contest' :
                      'Completed') :
                  fight.status}
              </Badge>
              {/* Round badge hidden for MVP */}
              {false && fight.status === 'active' && fight.currentRound && (
                <Badge colorScheme="orange">
                  Round {fight.currentRound}
                </Badge>
              )}
            </HStack>
          </VStack>
        </HStack>

        <HStack spacing={4} align="start">
          {/* Vote Statistics */}
          <VStack spacing={2} align="stretch" minW="200px">
            <Text fontSize="xs" fontWeight="medium" color="gray.300" textAlign="center">
              Vote Count
            </Text>
            <HStack spacing={4} justify="space-between">
              <VStack spacing={0} align="center" flex={1}>
                <Text fontSize="xs" color="gray.400" noOfLines={1}>
                  {fight.fighter1?.name}
                </Text>
                <HStack spacing={1}>
                  <Text fontSize="lg" fontWeight="bold" color="blue.400">
                    {getVoteCount(fight.fighter1?.id)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">votes</Text>
                </HStack>
              </VStack>
              <Divider orientation="vertical" height="40px" />
              <VStack spacing={0} align="center" flex={1}>
                <Text fontSize="xs" color="gray.400" noOfLines={1}>
                  {fight.fighter2?.name}
                </Text>
                <HStack spacing={1}>
                  <Text fontSize="lg" fontWeight="bold" color="blue.400">
                    {getVoteCount(fight.fighter2?.id)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">votes</Text>
                </HStack>
              </VStack>
            </HStack>
            {getTotalVotes() > 0 && (
              <Text fontSize="xs" color="gray.500" textAlign="center">
                Total: {getTotalVotes()} votes
              </Text>
            )}
          </VStack>

          {/* Winner Selection */}
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" fontWeight="medium" color="gray.300" textAlign="center">
              Who won this fight?
            </Text>
            <Select
              size="sm"
              placeholder="Select winner..."
              value={fight.winnerId || ''}
              onChange={(e) => onWinnerSelect(fight.id, e.target.value)}
              isDisabled={loading}
              minW="140px"
            >
              <option value={fight.fighter1?.id}>
                {fight.fighter1?.name || 'Fighter 1'}
              </option>
              <option value={fight.fighter2?.id}>
                {fight.fighter2?.name || 'Fighter 2'}
              </option>
              <option value="draw">Draw</option>
              <option value="no-contest">No Contest</option>
            </Select>
          </VStack>

          {/* Delete Fight Button */}
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" fontWeight="medium" color="gray.300" textAlign="center">
              Actions
            </Text>
            <IconButton
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={() => onDeleteFight(fight)}
              isDisabled={loading}
              aria-label="Delete fight"
              title="Delete this fight"
            />
          </VStack>
        </HStack>
      </HStack>
    </Box>
  );
};

export const EventManagement = ({ event, onEventUpdate }) => {
  const dataStore = useDataStore();
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingFighter, setEditingFighter] = useState(null);
  const [editingFight, setEditingFight] = useState(null);
  const [fightToDelete, setFightToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [coverImage, setCoverImage] = useState(event.coverImage || null);
  const [imageUploading, setImageUploading] = useState(false);
  const toast = useToast();

  // Get fights from DataStore instead of local state
  const fights = dataStore.getFightsForEvent(event.id);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isFighterEditOpen,
    onOpen: onFighterEditOpen,
    onClose: onFighterEditClose
  } = useDisclosure();
  const {
    isOpen: isFightEditOpen,
    onOpen: onFightEditOpen,
    onClose: onFightEditClose
  } = useDisclosure();
  const {
    isOpen: isDeleteFightModalOpen,
    onOpen: onDeleteFightModalOpen,
    onClose: onDeleteFightModalClose
  } = useDisclosure();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const generateQRCode = useCallback(async () => {
    try {
      // Use production domain for QR codes, even when developing locally
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const baseUrl = isLocalhost ? 'https://fighterbetplus.com' : window.location.origin;

      // Use slug URL if available for shorter, more scannable QR codes
      // Use display version (event.slug) for better marketing appearance
      const eventUrl = event.slug
        ? `${baseUrl}/${event.slug}`
        : `${baseUrl}/event/${event.id}`;

      const qrUrl = await QRCode.toDataURL(eventUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  }, [event.id, event.slug]);

  // Fights are now managed by DataStore - no subscription needed

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  const handleDragEnd = async (dragEvent) => {
    const { active, over } = dragEvent;

    // Check if we have a valid drop target
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = fights.findIndex(fight => fight.id === active.id);
    const newIndex = fights.findIndex(fight => fight.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically reorder fights for immediate UI feedback
    const newFights = arrayMove(fights, oldIndex, newIndex);

    try {
      // Update the order in the database
      const fightIds = newFights.map(fight => fight.id);
      await fightService.reorderFights(event.id, fightIds);

      toast({
        title: 'Fight order updated!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      // DataStore will automatically update via real-time subscription
    } catch (error) {
      toast({
        title: 'Failed to update fight order',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleWinnerSelect = async (fightId, winnerId) => {
    setLoading(true);
    try {
      // Determine the new status based on winner selection
      const newStatus = winnerId ? 'completed' : 'upcoming';

      // Update fight with winner and status
      await fightService.updateFight(fightId, {
        winnerId: winnerId || null,
        status: newStatus
      });

      const winnerText = winnerId === 'draw' ? 'Draw' :
        winnerId === 'no-contest' ? 'No Contest' :
          winnerId ? 'Winner selected' : 'Fight reset';

      toast({
        title: `${winnerText}!`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      // Real-time subscription will automatically update the fight
    } catch (error) {
      toast({
        title: 'Failed to update fight result',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditFighter = (fighter) => {
    console.log('Editing fighter:', fighter); // Debug log
    setEditingFighter(fighter);
    onFighterEditOpen();
  };

  const handleEditFight = (fight) => {
    setEditingFight(fight);
    onFightEditOpen();
  };

  const handleDeleteFight = (fight) => {
    setFightToDelete(fight);
    onDeleteFightModalOpen();
  };

  const handleDeleteFightConfirm = async () => {
    if (!fightToDelete) return;

    setDeleting(true);
    try {
      await fightService.deleteFight(fightToDelete.id);

      toast({
        title: 'Fight deleted successfully',
        description: `${fightToDelete.fighter1?.name} vs ${fightToDelete.fighter2?.name} has been removed.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Close modal and clear state
      onDeleteFightModalClose();
      setFightToDelete(null);

      // DataStore will automatically update via real-time subscription
    } catch (err) {
      toast({
        title: 'Failed to delete fight',
        description: err.message || 'An error occurred while deleting the fight.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = `${event.name}-qr-code.png`;
    link.href = qrCodeUrl;
    link.click();
  };

  const handleCoverImageChange = (file) => {
    setCoverImage(file);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setImageUploading(true);
    try {
      const imageUrl = await eventService.updateEventCoverImage(event.id, file);
      setCoverImage(imageUrl);

      toast({
        title: 'Cover image updated successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Notify parent component of the update
      onEventUpdate && onEventUpdate();
    } catch (error) {
      console.error('Image upload error:', error);
      toast({
        title: 'Failed to update cover image',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageRemove = async () => {
    setImageUploading(true);
    try {
      await eventService.removeEventCoverImage(event.id, coverImage);
      setCoverImage(null);

      toast({
        title: 'Cover image removed successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Notify parent component of the update
      onEventUpdate && onEventUpdate();
    } catch (error) {
      toast({
        title: 'Failed to remove cover image',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImageUploading(false);
    }
  };

  const viewEventPage = () => {
    window.open(`${window.location.origin}/${event.slug}`, '_blank');
  };

  return (
    <Box maxW="4xl" mx="auto" p={6}>
      <Card mb={6}>
        {/* Cover Image Display */}
        {event.coverImage && (
          <Box>
            <Image
              src={event.coverImage}
              alt={`${event.name} cover image`}
              width="100%"
              height="250px"
              objectFit="cover"
              borderTopRadius="lg"
            />
          </Box>
        )}
        <CardHeader>
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing={1}>
              <Heading size="lg">{event.name}</Heading>
              <Text color="gray.300">{event.location}</Text>
              <Text color="gray.400">
                {event.date instanceof Date
                  ? event.date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })
                  : 'Date TBD'
                }
              </Text>
            </VStack>
            <VStack>
              {qrCodeUrl && (
                <Box textAlign="center" display="flex" flexDirection="column" alignItems="center">
                  <img src={qrCodeUrl} alt="Event QR Code" style={{ maxWidth: '120px' }} />
                  <Button size="sm" mt={2} onClick={downloadQRCode}>
                    Download QR
                  </Button>
                  <Button size="sm" mt={2} onClick={viewEventPage}>
                    <span style={{ marginRight: '4px' }}>
                      View Event Page
                    </span>
                    <Icon as={FiExternalLink} boxSize={4} />
                  </Button>
                </Box>
              )}
            </VStack>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Badge colorScheme={event.isActive ? 'green' : 'red'}>
                {event.isActive ? 'Active' : 'Inactive'}
              </Badge>
              <HStack spacing={3}>
                <Button colorScheme="blue" onClick={onOpen}>
                  Add Fight
                </Button>
                {/* The bulk add fights is a hidden migration feature to locally add a list of fighters and fights */}
                {/* This is not to be used in production */}
                {/* <BulkAddFights eventId={event.id} onFightsAdded={onEventUpdate} /> */}
              </HStack>
            </HStack>

            {/* Cover Image Section */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.300">
                Event Cover Image
              </Text>
              <ImageUpload
                value={coverImage}
                onChange={handleCoverImageChange}
                onUploadStart={() => setImageUploading(true)}
                onUploadComplete={handleImageUpload}
                onUploadError={(error) => {
                  toast({
                    title: 'Upload failed',
                    description: error.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                  });
                }}
                placeholder="Upload a cover image for this event"
                aspectRatio="16/9"
                maxWidth="400px"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                This image will be displayed on the event page and in event listings
              </Text>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">Fights ({fights.length})</Heading>
            <VStack align="end" spacing={0}>
              <Text fontSize="sm" color="gray.400">
                Drag fights to reorder
              </Text>
              <Text fontSize="xs" color="gray.500">
                Select winners when fights are complete
              </Text>
            </VStack>
          </HStack>
        </CardHeader>
        <CardBody>
          {fights.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fights.map(fight => fight.id)}
                strategy={verticalListSortingStrategy}
              >
                <VStack spacing={3} align="stretch">
                  {fights.map((fight, index) => (
                    <SortableFightItem
                      key={fight.id}
                      fight={fight}
                      index={index}
                      onWinnerSelect={handleWinnerSelect}
                      onEditFighter={handleEditFighter}
                      onEditFight={handleEditFight}
                      onDeleteFight={handleDeleteFight}
                      loading={loading}
                      eventId={event.id}
                    />
                  ))}
                </VStack>
              </SortableContext>
            </DndContext>
          ) : (
            <Text textAlign="center" color="gray.400" py={8}>
              No fights added yet. Click "Add Fight" to get started.
            </Text>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Fight</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <AddFightForm
              eventId={event.id}
              onFightAdded={() => {
                onClose();
                // Real-time subscription will automatically update the fights list
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Fighter Edit Modal */}
      <Modal isOpen={isFighterEditOpen} onClose={() => {
        onFighterEditClose();
        setEditingFighter(null);
      }} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Edit Fighter
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FighterEditForm
              fighter={editingFighter}
              onSave={async () => {
                // DataStore will automatically update via real-time subscription
                onFighterEditClose();
                setEditingFighter(null);
              }}
              onCancel={() => {
                onFighterEditClose();
                setEditingFighter(null);
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Fight Edit Modal */}
      <FightEditModal
        fight={editingFight}
        isOpen={isFightEditOpen}
        onClose={() => {
          onFightEditClose();
          setEditingFight(null);
        }}
      />

      {/* Delete Fight Confirmation Modal */}
      <Modal isOpen={isDeleteFightModalOpen} onClose={onDeleteFightModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Fight</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="start" spacing={4}>
              <Text fontSize="md" color="white">
                Are you sure you want to delete the fight between{' '}
                <Text as="span" fontWeight="bold" color="white">
                  {fightToDelete?.fighter1?.name}
                </Text>
                {' '}and{' '}
                <Text as="span" fontWeight="bold" color="white">
                  {fightToDelete?.fighter2?.name}
                </Text>
                ?
              </Text>
              <Alert status="warning" borderRadius="md" bg="orange.900" borderColor="orange.600" borderWidth="1px">
                <AlertIcon color="orange.400" />
                <VStack align="start" spacing={2} ml={2}>
                  <Text fontWeight="semibold" color="orange.100">
                    This action cannot be undone.
                  </Text>
                  <Text fontSize="sm" color="orange.200" fontWeight="medium">
                    This will permanently delete:
                  </Text>
                  <VStack align="start" spacing={1} pl={2}>
                    <Text fontSize="sm" color="orange.100">
                      • The fight and all its details
                    </Text>
                    <Text fontSize="sm" color="orange.100">
                      • All user votes for this fight
                    </Text>
                  </VStack>
                </VStack>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteFightModalClose} isDisabled={deleting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteFightConfirm}
              isLoading={deleting}
              loadingText="Deleting..."
            >
              Delete Fight
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};


// Fight Edit Modal Component
const FightEditModal = ({ fight, isOpen, onClose }) => {
  const [weightClass, setWeightClass] = useState('');
  const [rounds, setRounds] = useState('3');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const weightClasses = [
    'Strawweight', 'Flyweight', 'Bantamweight', 'Featherweight',
    'Lightweight', 'Welterweight', 'Middleweight', 'Light Heavyweight',
    'Heavyweight', 'Super Heavyweight'
  ];

  useEffect(() => {
    if (fight) {
      setWeightClass(fight.weightClass || '');
      setRounds(fight.rounds?.toString() || '3');
    }
  }, [fight]);

  const handleSave = async () => {
    if (!fight) return;

    setLoading(true);
    try {
      await fightService.updateFight(fight.id, {
        weightClass,
        rounds: parseInt(rounds)
      });

      toast({
        title: 'Fight updated successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // DataStore will automatically update via real-time subscription
      onClose();
    } catch (error) {
      toast({
        title: 'Failed to update fight',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Fight Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Weight Class</FormLabel>
              <Select
                value={weightClass}
                onChange={(e) => setWeightClass(e.target.value)}
                placeholder="Select weight class"
              >
                {weightClasses.map(wc => (
                  <option key={wc} value={wc}>{wc}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Rounds</FormLabel>
              <Select
                value={rounds}
                onChange={(e) => setRounds(e.target.value)}
              >
                <option value="1">1 Round</option>
                <option value="3">3 Rounds</option>
                <option value="5">5 Rounds</option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={loading}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
